import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { getArticleSchema } from '../../utils/seoSchemas';
import { EDITORIAL_AUTHOR, INSIGHTS_MODIFIED, INSIGHTS_PUBLISHED } from './constants';

const Methodology = () => {
  const path = '/insights/methodology';
  const title = 'Global Trade Index (GTI) Methodology | TradeRadar';
  const description =
    'How TradeRadar ingests public trade news and documents, classifies events, and composes the Global Trade Index (GTI)—including refresh cadence, limitations, and how to interpret scores.';

  const jsonLd = getArticleSchema({
    path,
    seo_title: title,
    seo_description: description,
    datePublished: INSIGHTS_PUBLISHED,
    dateModified: INSIGHTS_MODIFIED,
    authorPerson: EDITORIAL_AUTHOR,
  });

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEOHead
        title={title}
        description={description}
        path={path}
        type="article"
        author={EDITORIAL_AUTHOR.name}
        publishedTime={`${INSIGHTS_PUBLISHED}T12:00:00.000Z`}
        modifiedTime={`${INSIGHTS_MODIFIED}T12:00:00.000Z`}
        keywords="GTI methodology, global trade index, trade event scoring, TradeRadar data pipeline"
        jsonLd={jsonLd}
      />

      <header className="mb-8">
        <p className="text-xs text-slate-500 dark:text-on-surface-variant mb-2">
          Insights · Methodology · Published {INSIGHTS_PUBLISHED} · Updated {INSIGHTS_MODIFIED}
        </p>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface mb-3">
          How we build the Global Trade Index (GTI)
        </h1>
        <p className="text-sm text-slate-600 dark:text-on-surface-variant">
          By {EDITORIAL_AUTHOR.name} ({EDITORIAL_AUTHOR.jobTitle})
        </p>
      </header>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Purpose</h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          The Global Trade Index (GTI) is a composite, country-level signal designed to summarise how much
          friction appears in the public record for a given economy: tariffs, sanctions, import or export
          bans, anti-dumping cases, disputes, and related policy shocks. It is not a forecast of GDP, FX, or
          asset prices. It is a structured lens on <em>documented</em> policy and enforcement activity so you
          can compare jurisdictions and drill into underlying events.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Inputs and sources</h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-2">
          TradeRadar aggregates machine-readable signals from open web sources: trade and general news wires,
          official gazettes where accessible, regulator bulletins, and multilateral announcements. Each item
          passes through extraction pipelines that attempt to identify event type, affected sectors,
          countries, approximate rates or scopes, and effective dates. Where the source text is ambiguous,
          we store conservative fields and prefer ranges or nulls over false precision.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          Because coverage depends on language, paywalls, and crawler access, the absence of an event in our
          database does not prove the absence of a real-world measure. Users should treat the platform as a
          triage layer that points back to citations—not a legal or compliance system of record.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Event taxonomy</h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-2">
          Events are normalised into a fixed vocabulary (for example tariff increases or decreases, sanctions,
          import or export bans, quotas, subsidies, anti-dumping actions, and formal disputes). Each type
          carries a default severity weight tuned so that rare but structural measures (such as sweeping bans)
          move scores more than incremental rate tweaks, while still preserving sensitivity to sustained
          tariff pressure.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          Sector tags are inferred from text and HS-style hints where present. Misclassification is possible;
          we correct systematic errors when identified. See our{' '}
          <Link to="/editorial-standards" className="text-primary-600 dark:text-primary hover:underline">
            editorial standards
          </Link>{' '}
          for how to report issues.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Scoring and aggregation</h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-2">
          Country scores aggregate weighted events over a rolling lookback window, with time decay so older
          measures gradually cede importance to newer ones unless they remain legally active. Bilateral
          events are attributed to both affected parties where the text supports it. Regional and global
          measures may appear on multiple country profiles when the scope is explicit.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          The 0–100 scale is calibrated for comparability across countries, not for absolute “good” or “bad”
          trade policy: a high score indicates relatively fewer detected friction events under our weights; a
          low score indicates more or heavier events. Always read the underlying event list before drawing
          operational conclusions.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Refresh cadence</h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          Pipelines run on a scheduled basis and may batch overnight depending on infrastructure load. Live
          dashboards may therefore lag primary sources by hours. Critical incident pages (such as chokepoint
          hubs) may receive more frequent enrichment when editors attach curated context atop automated
          feeds.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Known limitations</h2>
        <ul className="text-sm text-slate-700 dark:text-on-surface-variant space-y-2 list-disc list-inside">
          <li>Large-language-model extraction can miss nuance; humans spot-check samples but do not read every row.</li>
          <li>Paywalled or non-Latin sources may be underrepresented relative to English-language media.</li>
          <li>GTI does not yet encode non-tariff barriers that never appear as structured text (tacit delays, informal holds).</li>
          <li>Scores are not investment advice and must not be used as sole inputs to compliance programmes.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Further reading</h2>
        <ul className="text-sm text-slate-700 dark:text-on-surface-variant space-y-2">
          <li>
            <Link to="/insights" className="text-primary-600 dark:text-primary hover:underline">
              All insights articles
            </Link>
          </li>
          <li>
            <Link to="/insights/tariffs-vs-legislation" className="text-primary-600 dark:text-primary hover:underline">
              Tariffs vs. legislation modules
            </Link>
          </li>
          <li>
            <Link to="/contact" className="text-primary-600 dark:text-primary hover:underline">
              Contact the team
            </Link>
          </li>
        </ul>
      </section>
    </article>
  );
};

export default Methodology;
